import React, { FC, useState } from "react";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useTranslation } from "react-i18next";
import styles from "@/components/headerbar/languageSettings.module.css";
import { api } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useDispatch } from "react-redux";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import i18n from "@/translations/i18n";
import { setPreferredLanguage } from "@/components/state/parent/loginSlice";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem } from "@mui/material";

interface LanguageQuickSettingsProps {
  currentLang: any;
  userId: any;
}

const LanguageQuickSettings: FC<LanguageQuickSettingsProps> = ({
  currentLang,
  userId,
}) => {
  const { t } = useTranslation();
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const { handleError, errorModal } = useErrorModal();

  const { showConfirmationModalAsync } = useConfirmationModal();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const languageOptions = [
    {
      value: "de",
      label: t("language_german"),
      countryCode: "de",
    },
    {
      value: "en",
      label: t("language_english"),
      countryCode: "gb",
    },
  ];

  const handleCloseMenu = () => {
    setAnchorPosition(null);
  };

  const handleLanguageChange = async (selectedOption: any) => {
    if (!selectedOption || !selectedOption.value) return;

    const newLanguage = selectedOption.value;

    handleCloseMenu();

    if (newLanguage === currentLang) return;

    const confirmLanguageChange = await showConfirmationModalAsync(
      t("confirm_language_change_reload")
    );
    if (!confirmLanguageChange) {
      return;
    }

    i18n.changeLanguage(newLanguage);
    dispatch(setPreferredLanguage(newLanguage));

    submitLanguageChange(newLanguage);
  };

  const handleReloadAfterLanguageChange = () => {
    navigate(0);
  };

  const submitLanguageChange = async (newLan: any) => {
    const result = { preferred_language: newLan };
    api.genericApiRequest({
      entity: "user",
      method: "put",
      entityId: userId,
      submitData: result,
      successHandler: () => {
        // handleLogoutAfterLanguageChange();
        handleReloadAfterLanguageChange();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  function LanguageButton() {
    const langToFlagClass: Record<string, string> = {
      de: "fi fi-de ",
      en: "fi fi-gb",
    };
    const flagClass = langToFlagClass[currentLang] || "fi fi-de";

    return (
      <GenericButton
        variant="icon"
        color="white"
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          const rect = event.currentTarget.getBoundingClientRect();
          setAnchorPosition({ top: rect.bottom, left: rect.right });
        }}
        aria-controls={anchorPosition ? "languageMenu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorPosition ? "true" : undefined}
      >
        <span className={flagClass} />
      </GenericButton>
    );
  }

  return (
    <div className={styles.language_selector}>
      <LanguageButton />
      <Menu
        id="languageMenu"
        anchorReference="anchorPosition"
        anchorPosition={
          anchorPosition
            ? { top: anchorPosition.top, left: anchorPosition.left }
            : undefined
        }
        open={Boolean(anchorPosition)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 16,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {languageOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleLanguageChange(option)}
            selected={option.value === currentLang}
          >
            <span
              className={`fi fi-${option.countryCode}`}
              style={{ marginRight: "0.5rem" }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      {errorModal}
    </div>
  );
};

export default LanguageQuickSettings;
